/**
 * Coalesce MatrixCall.sendMetadataUpdate traffic to avoid Synapse M_LIMIT_EXCEEDED (429)
 * on m.call.sdp_stream_metadata_changed / org.matrix.call.sdp_stream_metadata_changed
 * (MSC3077) during mute/camera churn.
 *
 * matrix-js-sdk already batches ICE candidates for rate limits, but metadata updates fire
 * immediately. We patch per call instance (WeakMap-backed) so all SDK mute/hold paths are
 * covered without forking matrix-js-sdk — same app-level approach as CallSession lifecycle.
 *
 * CS API rate limiting (v1.10+): on M_LIMIT_EXCEEDED / 429, wait Retry-After (header) or
 * deprecated retry_after_ms, then retry. Local mute/camera is already applied before
 * sendMetadataUpdate in the SDK, so awaiters resolve on 429 and delivery retries in the
 * background (avoids app media-control withTimeout / Sentry noise).
 */

import { addBreadcrumb } from 'shared/sentry';
import {
  CALL_METADATA_UPDATE_DEBOUNCE_MS,
  CALL_METADATA_UPDATE_DEFAULT_RETRY_MS,
  CALL_METADATA_UPDATE_MAX_RETRIES,
  CALL_METADATA_UPDATE_MAX_WAIT_MS,
  CALL_METADATA_UPDATE_MAX_RETRY_AFTER_MS,
} from '../matrixConstants';

/** @type {WeakMap<object, { dispose: () => void }>} */
const patchedCalls = new WeakMap();

const logMetadataRateLimit = (message, data = {}) => {
  try {
    addBreadcrumb({
      category: 'matrix.call.metadata',
      message,
      data,
      level: 'warning',
    });
  } catch (_) {}
};

/**
 * Detect Matrix / HTTP rate-limit errors.
 * Prefer matrix-js-sdk HTTPError/MatrixError#isRateLimitError (M_LIMIT_EXCEEDED or HTTP 429).
 */
export const isMatrixRateLimitError = (err) => {
  if (!err || typeof err !== 'object') return false;
  if (typeof err.isRateLimitError === 'function') {
    try {
      if (err.isRateLimitError()) return true;
    } catch (_) {
      // Fall through to structural checks.
    }
  }
  if (err.httpStatus === 429 || err.statusCode === 429) return true;
  if (err.errcode === 'M_LIMIT_EXCEEDED') return true;
  const msg = String(err.message || err.msg || '');
  // Prefer structured fields above; only match explicit Matrix rate-limit wording.
  return msg.includes('M_LIMIT_EXCEEDED') || /\[\s*429\s*]/.test(msg);
};

/**
 * Resolve Matrix retry delay with a hard upper bound.
 * Spec (CS API rate limiting / v1.10+): prefer Retry-After header, then deprecated
 * body retry_after_ms. Mirrors matrix-js-sdk MatrixError#getRetryAfterMs /
 * safeGetRetryAfterMs (duck-typed so Jest need not load SDK ESM helpers).
 */
export const getMatrixRetryAfterMs = (
  err,
  defaultMs = CALL_METADATA_UPDATE_DEFAULT_RETRY_MS,
  maxMs = CALL_METADATA_UPDATE_MAX_RETRY_AFTER_MS
) => {
  const cappedDefault = Math.max(0, Number(defaultMs) || 0);
  const cappedMax = Math.max(cappedDefault, Number(maxMs) || cappedDefault);
  let raw = cappedDefault;
  let resolved = false;

  if (typeof err?.getRetryAfterMs === 'function') {
    try {
      const ms = err.getRetryAfterMs();
      if (Number.isFinite(ms) && ms >= 0) {
        raw = ms;
        resolved = true;
      }
    } catch (_) {
      // Invalid Retry-After / retry_after_ms — fall through like safeGetRetryAfterMs.
    }
  }

  if (!resolved) {
    const fromData = err?.data?.retry_after_ms;
    if (Number.isFinite(fromData) && fromData >= 0) raw = fromData;
  }

  return Math.min(Math.max(0, raw), cappedMax);
};

const settleBatch = (batch, error) => {
  if (!Array.isArray(batch) || batch.length === 0) return;
  if (error) {
    batch.forEach(({ reject }) => {
      try {
        reject(error);
      } catch (_) {}
    });
    return;
  }
  batch.forEach(({ resolve }) => {
    try {
      resolve();
    } catch (_) {}
  });
};

const normalizeTiming = (debounceMs, maxWaitMs) => {
  const debounce = Math.max(0, Number(debounceMs) || 0);
  // maxWait must be at least debounce or the starvation guard never extends past trailing debounce.
  const maxWait = Math.max(debounce, Number(maxWaitMs) || debounce);
  return { debounce, maxWait };
};

/**
 * Patch a MatrixCall instance so sendMetadataUpdate is debounced (with maxWait),
 * coalesced while in-flight / backing off, and retried on 429 per CS API guidance.
 * Idempotent per call object. Returns a dispose function that restores the original method.
 *
 * @param {import('matrix-js-sdk/lib/webrtc/call').MatrixCall | null | undefined} call
 * @param {{
 *   debounceMs?: number,
 *   maxWaitMs?: number,
 *   maxRetries?: number,
 *   defaultRetryMs?: number,
 *   maxRetryAfterMs?: number,
 * }} [options]
 * @returns {() => void}
 */
export function patchCallMetadataRateLimit(call, options = {}) {
  if (!call || typeof call.sendMetadataUpdate !== 'function') return () => {};

  const existing = patchedCalls.get(call);
  if (existing) return existing.dispose;

  const { debounce: debounceMs, maxWait: maxWaitMs } = normalizeTiming(
    options.debounceMs ?? CALL_METADATA_UPDATE_DEBOUNCE_MS,
    options.maxWaitMs ?? CALL_METADATA_UPDATE_MAX_WAIT_MS
  );
  const maxRetries = Math.max(0, Number(options.maxRetries ?? CALL_METADATA_UPDATE_MAX_RETRIES) || 0);
  const defaultRetryMs = options.defaultRetryMs ?? CALL_METADATA_UPDATE_DEFAULT_RETRY_MS;
  const maxRetryAfterMs = options.maxRetryAfterMs ?? CALL_METADATA_UPDATE_MAX_RETRY_AFTER_MS;

  const originalFn = call.sendMetadataUpdate;
  const original = () => originalFn.call(call);

  let debounceTimer = null;
  let retryTimer = null;
  let inFlight = false;
  let needsResend = false;
  let disposed = false;
  let retryCount = 0;
  /** One extra background send after retry budget is exhausted (peer mute badge convergence). */
  let postLimitRecoveryScheduled = false;
  let queueStartedAt = 0;
  /** @type {Array<{ resolve: (v?: unknown) => void, reject: (e: unknown) => void }>} */
  let waiters = [];

  const clearDebounceTimer = () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  };

  const clearRetryTimer = () => {
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  const clearTimers = () => {
    clearDebounceTimer();
    clearRetryTimer();
  };

  const scheduleFollowUp = (delayMs) => {
    if (disposed || retryTimer) return;
    clearDebounceTimer();
    retryTimer = setTimeout(() => {
      retryTimer = null;
      void flush();
    }, Math.max(0, delayMs));
  };

  /**
   * Schedule a trailing flush. Uses maxWait so continuous mute/camera toggles cannot
   * reset the timer forever and starve the peer of metadata.
   * While a 429 backoff timer is active, only queue waiters — do not race the backoff.
   */
  const scheduleDebouncedFlush = () => {
    if (disposed || retryTimer) return;

    const now = Date.now();
    if (!queueStartedAt) queueStartedAt = now;

    const elapsed = now - queueStartedAt;
    const remainingMaxWait = Math.max(0, maxWaitMs - elapsed);
    const delay = Math.min(debounceMs, remainingMaxWait);

    clearDebounceTimer();
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      void flush();
    }, delay);
  };

  const scheduleRateLimitRetry = (err, { recovery = false } = {}) => {
    needsResend = true;
    const delay = getMatrixRetryAfterMs(err, defaultRetryMs, maxRetryAfterMs);
    logMetadataRateLimit(
      recovery
        ? 'sdp metadata rate-limited; scheduling recovery send'
        : 'sdp metadata rate-limited; retrying',
      {
        callId: call.callId ?? null,
        attempt: retryCount,
        maxRetries,
        delayMs: delay,
        recovery,
      }
    );
    scheduleFollowUp(delay);
  };

  const flush = async () => {
    if (disposed) return;
    if (inFlight) {
      needsResend = true;
      return;
    }
    if (waiters.length === 0 && !needsResend) return;

    inFlight = true;
    needsResend = false;
    clearDebounceTimer();

    // This flush owns the currently queued waiters. New callers during the request
    // land in `waiters` again and set needsResend via the inFlight branch / queue.
    const flushWaiters = waiters;
    waiters = [];
    queueStartedAt = 0;

    try {
      if (typeof call.callHasEnded === 'function' && call.callHasEnded()) {
        settleBatch(flushWaiters, null);
        retryCount = 0;
        postLimitRecoveryScheduled = false;
        return;
      }

      await original();
      retryCount = 0;
      postLimitRecoveryScheduled = false;
      if (disposed) {
        settleBatch(flushWaiters, null);
        return;
      }

      settleBatch(flushWaiters, null);

      if (needsResend || waiters.length > 0) {
        needsResend = false;
        scheduleFollowUp(debounceMs);
      }
    } catch (err) {
      if (disposed) {
        settleBatch(flushWaiters, null);
        return;
      }

      if (isMatrixRateLimitError(err) && retryCount < maxRetries) {
        // CS API: wait Retry-After then retry the send. Local media already applied;
        // resolve awaiters now so setMicrophoneMuted / setLocalVideoMuted (and app
        // withTimeout ~1.8s) are not blocked by backoff.
        settleBatch(flushWaiters, null);
        retryCount += 1;
        scheduleRateLimitRetry(err);
        return;
      }

      if (isMatrixRateLimitError(err)) {
        // Best-effort for awaiters; one extra background send so the peer mute/camera
        // badge can converge without requiring another user toggle.
        settleBatch(flushWaiters, null);
        if (!postLimitRecoveryScheduled) {
          postLimitRecoveryScheduled = true;
          // Keep retryCount exhausted so a failed recovery does not reopen the budget.
          retryCount = maxRetries;
          scheduleRateLimitRetry(err, { recovery: true });
          return;
        }
        logMetadataRateLimit('sdp metadata rate-limited; giving up', {
          callId: call.callId ?? null,
          maxRetries,
        });
        retryCount = 0;
        // Fresh user toggles during this attempt stay queued in `waiters` for finally.
        return;
      }

      settleBatch(flushWaiters, err);
      retryCount = 0;
      postLimitRecoveryScheduled = false;
    } finally {
      inFlight = false;
      if (!disposed && (needsResend || waiters.length > 0) && !retryTimer && !debounceTimer) {
        // New toggles after hard give-up get a fresh retry budget.
        if (waiters.length > 0) {
          postLimitRecoveryScheduled = false;
          retryCount = 0;
        }
        scheduleFollowUp(debounceMs);
      }
    }
  };

  call.sendMetadataUpdate = function patchedSendMetadataUpdate() {
    if (disposed) return original();
    if (typeof call.callHasEnded === 'function' && call.callHasEnded()) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      waiters.push({ resolve, reject });
      if (inFlight) {
        needsResend = true;
        return;
      }
      scheduleDebouncedFlush();
    });
  };

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    clearTimers();
    // Call teardown: do not reject mute/camera awaiters (would surface as UI errors).
    const pending = waiters;
    waiters = [];
    queueStartedAt = 0;
    needsResend = false;
    retryCount = 0;
    postLimitRecoveryScheduled = false;
    settleBatch(pending, null);
    try {
      call.sendMetadataUpdate = originalFn;
    } catch (_) {}
    patchedCalls.delete(call);
  };

  patchedCalls.set(call, { dispose });
  return dispose;
}
